// src/features/datev/components/DatevExchange.tsx

import React, { useState, useEffect, useCallback } from 'react';
import ScopedMonthEmployeeSyncPanel from './ScopedMonthEmployeeSyncPanel';
import {
  Search, RefreshCw, AlertCircle, Users, UserPlus,
  CheckCircle, Clock, XCircle, ArrowRight
} from 'lucide-react';
import {
  datevExchangeService,
  DatevExchangeEmployee,
  DatevJobState,
  DatevTargetSystem
} from '../../../services/datev/DatevExchangeService';
import { EmployeeCard, DatevErrorPanel } from './EmployeeCard';
import { supabase } from '../../../shared/services/supabase';
import { netlifyFetch } from '../../../shared/services/netlifyFetch';
import { verdecke } from '../werteSchutz';

interface DatevExchangeProps {
  customerId: string;
  beraternummer: string;
  mandantennummer: string;
  datevLohnsystem: string;
}

type ViewMode = 'overview' | 'read' | 'create';

interface JobProgress {
  jobUuid: string;
  state: DatevJobState;
  pollNumber: number;
  secondsElapsed: number;
  startedAt: number;
}

export const DatevExchange: React.FC<DatevExchangeProps> = ({
  customerId,
  beraternummer,
  mandantennummer,
  datevLohnsystem
}) => {
  const clientId = `${beraternummer}-${mandantennummer}`;
  // Target-System aus dem Lohnsystem des Mandanten (customers.abrechnungsmethode)
  const targetSystem: DatevTargetSystem = datevLohnsystem === 'lodas' ? 'lodas' : 'lug';

  // ============================================
  // STATE
  // ============================================

  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [accessHelpUrl, setAccessHelpUrl] = useState<string | null>(null);
  const [availableClients, setAvailableClients] = useState<{ consultant: string; ranges: string[]; count: number }[] | null>(null);

  const [view, setView] = useState<ViewMode>('overview');

  // Lese-Workflow
  const [readMonth, setReadMonth] = useState<string>('2026-06');
  const [readResourceName, setReadResourceName] = useState<string>('employees');
  const [readJobProgress, setReadJobProgress] = useState<JobProgress | null>(null);
  const [readResult, setReadResult] = useState<any>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [readHelpUrl, setReadHelpUrl] = useState<string | null>(null);

  // Schreibe-Workflow (Mitarbeiter anlegen)
  const [newEmpSurname, setNewEmpSurname] = useState('');
  const [newEmpFirstName, setNewEmpFirstName] = useState('');
  const [newEmpSex, setNewEmpSex] = useState<'M' | 'W' | 'D'>('W');
  const [newEmpStartDate, setNewEmpStartDate] = useState('2026-06-01');
  const [newEmpRefDate, setNewEmpRefDate] = useState('2026-06');
  const [newEmpPnr, setNewEmpPnr] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [createJobProgress, setCreateJobProgress] = useState<JobProgress | null>(null);
  const [createResult, setCreateResult] = useState<any>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createHelpUrl, setCreateHelpUrl] = useState<string | null>(null);

  // ============================================
  // MUST-PFLICHTFELDER MITARBEITER-NEUANLAGE
  // Vorbefüllt mit den Werten aus dem DATEV-Beispielmitarbeiter (gültige
  // Demo-Werte), alle editierbar. Mapping erfolgt in startCreateEmployee.
  // ============================================
  // Grunddaten
  const [newEmpCompanyPnr, setNewEmpCompanyPnr] = useState('BX1dd2');
  const [newEmpBusinessUnitId, setNewEmpBusinessUnitId] = useState('1');
  const [newEmpPaymentMethod, setNewEmpPaymentMethod] = useState('5');
  // Beschäftigungszeitraum
  const [newEmpEndDate, setNewEmpEndDate] = useState('2099-12-31');
  // Persönliche Angaben
  const [newEmpNamePrefix, setNewEmpNamePrefix] = useState('');
  const [newEmpNameAffix, setNewEmpNameAffix] = useState('');
  const [newEmpEmail, setNewEmpEmail] = useState('max.mustermann@example.de');
  const [newEmpPhone, setNewEmpPhone] = useState('+49 911 1234567');
  const [newEmpSsn, setNewEmpSsn] = useState('15070649C103');
  const [newEmpDob, setNewEmpDob] = useState('2000-01-02');
  const [newEmpNationality, setNewEmpNationality] = useState('000');
  // Adresse
  const [newEmpStreet, setNewEmpStreet] = useState('Bahnhofstr.');
  const [newEmpHouseNumber, setNewEmpHouseNumber] = useState('12a');
  const [newEmpPostalCode, setNewEmpPostalCode] = useState('90429');
  const [newEmpCity, setNewEmpCity] = useState('Nuernberg');
  const [newEmpCountry, setNewEmpCountry] = useState('D');
  // Bankverbindung
  const [newEmpIban, setNewEmpIban] = useState('DE02500105170137075030');
  const [newEmpBic, setNewEmpBic] = useState('GENODEF1P17');
  // Arbeitszeit
  const [newEmpWeeklyHours, setNewEmpWeeklyHours] = useState('25.00');
  const [newEmpHoursMon, setNewEmpHoursMon] = useState('5.00');
  const [newEmpHoursTue, setNewEmpHoursTue] = useState('5.00');
  const [newEmpHoursWed, setNewEmpHoursWed] = useState('5.00');
  const [newEmpHoursThu, setNewEmpHoursThu] = useState('5.00');
  const [newEmpHoursFri, setNewEmpHoursFri] = useState('5.00');
  const [newEmpHoursSat, setNewEmpHoursSat] = useState('0.00');
  const [newEmpHoursSun, setNewEmpHoursSun] = useState('0.00');
  // Tätigkeit / Tätigkeitsschlüssel
  const [newEmpActivityType, setNewEmpActivityType] = useState('1');
  const [newEmpEmployeeType, setNewEmpEmployeeType] = useState('101');
  const [newEmpPersonnelLeasing, setNewEmpPersonnelLeasing] = useState('0');
  const [newEmpOccupationalTitle, setNewEmpOccupationalTitle] = useState('DevOps Engineer');
  const [newEmpHighestProfTraining, setNewEmpHighestProfTraining] = useState('2');
  const [newEmpHighestEducation, setNewEmpHighestEducation] = useState('2');
  const [newEmpContractualStructure, setNewEmpContractualStructure] = useState('1');
  // Steueridentifikationsmerkmale
  const [newEmpTaxId, setNewEmpTaxId] = useState('12345678912');
  const [newEmpEmploymentType, setNewEmpEmploymentType] = useState('2');
  const [newEmpRequestedAllowance, setNewEmpRequestedAllowance] = useState('123');
  // Urlaubsanspruch
  const [newEmpBasicVacation, setNewEmpBasicVacation] = useState('30.0');
  const [newEmpCurrentYearVacation, setNewEmpCurrentYearVacation] = useState('30.0');
  // Optionaler Festbezug
  const [newEmpGrossId, setNewEmpGrossId] = useState('');
  const [newEmpGrossAmount, setNewEmpGrossAmount] = useState('');
  const [newEmpGrossSalaryTypeId, setNewEmpGrossSalaryTypeId] = useState('');
  const [newEmpGrossPaymentMonths, setNewEmpGrossPaymentMonths] = useState('');
  // Optionaler Stundenlohn
  const [newEmpHourlyId, setNewEmpHourlyId] = useState('');
  const [newEmpHourlyAmount, setNewEmpHourlyAmount] = useState('');

  // Persistenz-Prüfung nach Schreibvorgang (DATEV MUST)
  const [persistenceLoading, setPersistenceLoading] = useState(false);
  const [persistenceResult, setPersistenceResult] = useState<any>(null);
  const [persistenceError, setPersistenceError] = useState<string | null>(null);

  // Live-Countdown-Sekundenzähler
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // ============================================
  // BERECHTIGUNGSPRÜFUNG (DATEV MUST)
  // ============================================

  const checkAccess = useCallback(async () => {
    setAccessError(null);
    setAccessHelpUrl(null);
    setAccessChecked(false);
    try {
      await datevExchangeService.checkAccess({ customerId, clientId, targetSystem });
      setHasAccess(true);
      setAccessChecked(true);
    } catch (err: any) {
      setHasAccess(false);
      setAccessChecked(true);
      if (err.requires_reconnect) {
        setAccessError('Keine DATEV-Verbindung. Bitte zunächst im Tab "Verbindung" mit DATEV verbinden.');
      } else if (err.error) {
        setAccessError(err.error + ' — Bitte überprüfen Sie Berater- und Mandantennummer.');
      } else {
        setAccessError('Berechtigung konnte nicht geprüft werden.');
      }
      setAccessHelpUrl(err.help_url || null);

      // Audit-Auflage 2: Bei Berechtigungsfehler freigegebene Mandanten anzeigen
      try {
        const res = await netlifyFetch(
          `https://intranet.xtax.de/.netlify/functions/datev-exchange/clients?customerId=${customerId}`
        );
        if (res.ok) {
          const data = await res.json();
          if (data.groups && data.groups.length > 0) {
            setAvailableClients(data.groups);
          }
        }
      } catch (e) {
        // Mandanten-Liste optional
      }
    }
  }, [customerId, clientId, targetSystem]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  // ============================================
  // LESEN: Mitarbeiter abrufen (3-Phasen-Job)
  // ============================================

  const startReadEmployees = async () => {
    setReadError(null);
    setReadHelpUrl(null);
    setReadResult(null);

    try {
      // Phase 1: Job anlegen (DATEV MUST: alle 3 Ressourcentypen unterstützen)
      const job = await datevExchangeService.createReadJob(
        { customerId, clientId, targetSystem },
        { resource_name: readResourceName as any, reference_date: readMonth }
      );

      const startedAt = Date.now();
      setReadJobProgress({
        jobUuid: job.id,
        state: job.state,
        pollNumber: 0,
        secondsElapsed: 0,
        startedAt: startedAt
      });

      // Phase 2 + 3: Polling mit Live-Update
      const result = await datevExchangeService.pollJobUntilDone(
        { customerId, clientId, targetSystem },
        job.id,
        readResourceName,
        (state, pollNumber, secondsElapsed) => {
          setReadJobProgress({
            jobUuid: job.id,
            state: state,
            pollNumber: pollNumber,
            secondsElapsed: secondsElapsed,
            startedAt: startedAt
          });
        }
      );

      setReadResult(result);
      setReadJobProgress(prev => prev ? { ...prev, state: 'successful' } : null);
    } catch (err: any) {
      setReadError(err.error || 'Unbekannter Fehler');
      setReadHelpUrl(err.help_url || null);
      setReadJobProgress(prev => prev ? { ...prev, state: 'failed' } : null);
    }
  };

  // ============================================
  // SCHREIBEN: Mitarbeiter anlegen
  // ============================================

  const startCreateEmployee = async () => {
    setCreateError(null);
    setCreateHelpUrl(null);
    setCreateResult(null);
    setDuplicateWarning(null);
    setPersistenceResult(null);
    setPersistenceError(null);

    // DATEV MUST: alle Pflichtfelder müssen befüllt sein.
    // Vorsatzwort/Namenszusatz sind bewusst ausgenommen (dürfen leer bleiben).
    // Beschäftigungsbetrieb ist seit 14.07.2026 optional: LODAS-Bestände ohne
    // Betriebsstätten lehnen jeden Wert mit #DXLO02423 ab — leer lassen heißt
    // dann: Feld wird nicht mitgesendet.
    const requiredFields: { value: string; label: string }[] = [
      { value: newEmpSurname, label: 'Nachname' },
      { value: newEmpFirstName, label: 'Vorname' },
      { value: newEmpStartDate, label: 'Eintrittsdatum' },
      { value: newEmpEndDate, label: 'Austrittsdatum' },
      { value: newEmpRefDate, label: 'Reference-Date' },
      { value: newEmpCompanyPnr, label: 'Betriebliche Personalnummer' },
      { value: newEmpPaymentMethod, label: 'Zahlungsart' },
      { value: newEmpEmail, label: 'E-Mail' },
      { value: newEmpPhone, label: 'Telefon' },
      { value: newEmpSsn, label: 'SV-Nummer' },
      { value: newEmpDob, label: 'Geburtsdatum' },
      { value: newEmpNationality, label: 'Staatsangehörigkeit' },
      { value: newEmpStreet, label: 'Straße' },
      { value: newEmpHouseNumber, label: 'Hausnummer' },
      { value: newEmpPostalCode, label: 'PLZ' },
      { value: newEmpCity, label: 'Ort' },
      { value: newEmpCountry, label: 'Land' },
      { value: newEmpIban, label: 'IBAN' },
      { value: newEmpBic, label: 'BIC' },
      { value: newEmpWeeklyHours, label: 'Wöchentliche Arbeitszeit' },
      { value: newEmpHoursMon, label: 'Arbeitszeit Montag' },
      { value: newEmpHoursTue, label: 'Arbeitszeit Dienstag' },
      { value: newEmpHoursWed, label: 'Arbeitszeit Mittwoch' },
      { value: newEmpHoursThu, label: 'Arbeitszeit Donnerstag' },
      { value: newEmpHoursFri, label: 'Arbeitszeit Freitag' },
      { value: newEmpHoursSat, label: 'Arbeitszeit Samstag' },
      { value: newEmpHoursSun, label: 'Arbeitszeit Sonntag' },
      { value: newEmpActivityType, label: 'Arbeitnehmertyp' },
      { value: newEmpEmployeeType, label: 'Personengruppenschlüssel' },
      { value: newEmpPersonnelLeasing, label: 'Arbeitnehmerüberlassung' },
      { value: newEmpOccupationalTitle, label: 'Ausgeübte Tätigkeit' },
      { value: newEmpHighestProfTraining, label: 'Höchster Ausbildungsabschluss' },
      { value: newEmpHighestEducation, label: 'Höchster Schulabschluss' },
      { value: newEmpContractualStructure, label: 'Vertragsform' },
      { value: newEmpTaxId, label: 'Steuer-Identifikationsnummer' },
      { value: newEmpEmploymentType, label: 'Kennzeichnung Arbeitgeber' },
      { value: newEmpRequestedAllowance, label: 'Gewünschter jährl. Freibetrag' },
      { value: newEmpBasicVacation, label: 'Grundsätzlicher Urlaubsanspruch' },
      { value: newEmpCurrentYearVacation, label: 'Urlaubsanspruch aktuelles Jahr' }
    ];
    const missing = requiredFields.filter(f => !f.value || !String(f.value).trim()).map(f => f.label);
    if (missing.length > 0) {
      setCreateError('Bitte folgende Pflichtfelder ausfüllen: ' + missing.join(', '));
      return;
    }

    // DATEV MUST: Duplikat-Prüfung Personalnummer
    // Wenn eine PNr angegeben wurde und wir bereits Mitarbeiter geladen haben,
    // warnen wir bei Duplikaten (bevor der POST rausgeht)
    if (newEmpPnr && readResult?.employees) {
      const existing = readResult.employees.find(
        (emp: DatevExchangeEmployee) => String(emp.personnel_number) === newEmpPnr
      );
      if (existing) {
        setDuplicateWarning(
          `Personalnummer ${newEmpPnr} ist bereits vergeben an ${existing.first_name} ${existing.surname}. ` +
          `Ein zweiter POST mit derselben Personalnummer wird von DATEV abgelehnt.`
        );
        return;
      }
    }

    // DATEV MUST (verpflichtend, unabhängig von einem manuellen Leseabruf):
    // Duplikat-Prüfung gegen den DATEV-Spiegel (datev_employees) — der Spiegel
    // ist unser stehender Vollabruf (15-Min-Zyklus) und deckt damit die
    // Vorgabe "Abruf vor jeder Anlage" strukturell ab. Fail-closed: Wenn die
    // Prüfung selbst fehlschlägt, wird NICHT angelegt.
    if (newEmpPnr) {
      const { data: mirrorRows, error: mirrorError } = await supabase
        .from('datev_employees')
        .select('personalnummer, nachname, vorname')
        .eq('customer_id', customerId)
        .eq('beraternummer', beraternummer)
        .eq('mandantennummer', mandantennummer)
        .eq('personalnummer', newEmpPnr)
        .limit(1);

      if (mirrorError) {
        setCreateError('Duplikat-Prüfung gegen den DATEV-Spiegel fehlgeschlagen: ' + mirrorError.message + ' — Anlage abgebrochen (DATEV MUST).');
        return;
      }
      if (mirrorRows && mirrorRows.length > 0) {
        setDuplicateWarning(
          `Personalnummer ${newEmpPnr} ist laut DATEV-Spiegel bereits vergeben an ${mirrorRows[0].vorname} ${mirrorRows[0].nachname}. ` +
          `Ein zweiter POST mit derselben Personalnummer wird von DATEV abgelehnt.`
        );
        return;
      }
    }

    // Persönliche Angaben — Vorsatzwort/Namenszusatz nur mitgeben, wenn befüllt
    const personalData: any = {
      sex: newEmpSex,
      email: newEmpEmail,
      phone: newEmpPhone,
      social_security_number: newEmpSsn,
      date_of_birth: newEmpDob,
      nationality: newEmpNationality
    };
    if (newEmpNamePrefix.trim()) personalData.name_prefix = newEmpNamePrefix.trim();
    if (newEmpNameAffix.trim()) personalData.name_affix = newEmpNameAffix.trim();

    const employee: DatevExchangeEmployee = {
      surname: newEmpSurname,
      first_name: newEmpFirstName,
      company_personnel_number: newEmpCompanyPnr,
      // Beschäftigungsbetrieb nur mitsenden, wenn befüllt (LODAS-Bestände
      // ohne Betriebsstätten lehnen jeden Wert ab — #DXLO02423)
      ...(newEmpBusinessUnitId.trim() ? { business_unit_id: newEmpBusinessUnitId.trim() as any } : {}),
      payment_method: newEmpPaymentMethod,
      personal_data: personalData,
      address: {
        street: newEmpStreet,
        house_number: newEmpHouseNumber,
        postal_code: newEmpPostalCode,
        city: newEmpCity,
        country: newEmpCountry
      },
      account: {
        iban: newEmpIban,
        bic: newEmpBic
      },
      activity: {
        activity_type: newEmpActivityType,
        employee_type: parseInt(newEmpEmployeeType, 10),
        personnel_leasing: parseInt(newEmpPersonnelLeasing, 10),
        occupational_title: newEmpOccupationalTitle,
        highest_level_of_professional_training: parseInt(newEmpHighestProfTraining, 10),
        highest_level_of_education: parseInt(newEmpHighestEducation, 10),
        contractual_structure: parseInt(newEmpContractualStructure, 10),
        weekly_working_hours: parseFloat(newEmpWeeklyHours),
        allocation_of_working_hours_monday: parseFloat(newEmpHoursMon),
        allocation_of_working_hours_tuesday: parseFloat(newEmpHoursTue),
        allocation_of_working_hours_wednesday: parseFloat(newEmpHoursWed),
        allocation_of_working_hours_thursday: parseFloat(newEmpHoursThu),
        allocation_of_working_hours_friday: parseFloat(newEmpHoursFri),
        allocation_of_working_hours_saturday: parseFloat(newEmpHoursSat),
        allocation_of_working_hours_sunday: parseFloat(newEmpHoursSun)
      },
      taxation: {
        tax_identification_number: newEmpTaxId,
        employment_type: newEmpEmploymentType,
        requested_annual_allowance: parseInt(newEmpRequestedAllowance, 10)
      },
      vacation_entitlement: {
        basic_vacation_entitlement: parseFloat(newEmpBasicVacation),
        current_year_vacation_entitlement: parseFloat(newEmpCurrentYearVacation)
      },
      employment_periods: [{
        date_of_commencement_of_employment: newEmpStartDate,
        date_of_termination_of_employment: newEmpEndDate
      }]
    };

    // Optionale Personalnummer (DATEV: wenn leer, vergibt das Lohnsystem automatisch eine)
    if (newEmpPnr) {
      employee.personnel_number = parseInt(newEmpPnr, 10);
    }

    // Optionaler Festbezug (nur wenn Betrag gesetzt) — Struktur gemäß DATEV-Beispiel
    if (newEmpGrossAmount.trim()) {
      employee.gross_payments = [{
        id: newEmpGrossId ? parseInt(newEmpGrossId, 10) : 1,
        amount: parseFloat(newEmpGrossAmount),
        ...(newEmpGrossSalaryTypeId ? { salary_type_id: parseInt(newEmpGrossSalaryTypeId, 10) } : {}),
        ...(newEmpGrossPaymentMonths ? { payment_months: newEmpGrossPaymentMonths } : {})
      }];
    }

    // Optionaler Stundenlohn (nur wenn Betrag gesetzt)
    if (newEmpHourlyAmount.trim()) {
      (employee as any).hourly_wages = [{
        id: newEmpHourlyId ? parseInt(newEmpHourlyId, 10) : 1,
        amount: parseFloat(newEmpHourlyAmount)
      }];
    }

    try {
      // Phase 1: Mitarbeiter-Anlage starten (gibt Job-UUID zurück)
      const job = await datevExchangeService.createEmployee(
        { customerId, clientId, targetSystem },
        employee,
        newEmpRefDate
      );

      const startedAt = Date.now();
      setCreateJobProgress({
        jobUuid: job.id,
        state: job.state,
        pollNumber: 0,
        secondsElapsed: 0,
        startedAt: startedAt
      });

      // Phase 2 + 3: Polling
      const result = await datevExchangeService.pollJobUntilDone(
        { customerId, clientId, targetSystem },
        job.id,
        'employees',
        (state, pollNumber, secondsElapsed) => {
          setCreateJobProgress({
            jobUuid: job.id,
            state: state,
            pollNumber: pollNumber,
            secondsElapsed: secondsElapsed,
            startedAt: startedAt
          });
        }
      );

      setCreateResult(result);
      setCreateJobProgress(prev => prev ? { ...prev, state: 'successful' } : null);
    } catch (err: any) {
      setCreateError(err.error || 'Unbekannter Fehler');
      setCreateHelpUrl(err.help_url || null);
      setCreateJobProgress(prev => prev ? { ...prev, state: 'failed' } : null);
    }
  };

  // ============================================
  // PERSISTENZ-PRÜFUNG (DATEV MUST)
  // Nach jeder Neuanlage muss ein GET-Job durchgeführt werden,
  // um die Persistenz der Daten zu prüfen.
  // ============================================

  const startPersistenceCheck = async () => {
    setPersistenceLoading(true);
    setPersistenceResult(null);
    setPersistenceError(null);

    try {
      const job = await datevExchangeService.createReadJob(
        { customerId, clientId, targetSystem },
        { resource_name: 'employees', reference_date: newEmpRefDate || '2026-06' }
      );

      const result = await datevExchangeService.pollJobUntilDone(
        { customerId, clientId, targetSystem },
        job.id,
        'employees'
      );

      setPersistenceResult(result);
    } catch (err: any) {
      setPersistenceError(err.error || 'Persistenz-Prüfung fehlgeschlagen');
    } finally {
      setPersistenceLoading(false);
    }
  };

  // ============================================
  // RENDER-HELPER
  // ============================================

  const renderJobProgress = (progress: JobProgress | null) => {
    if (!progress) return null;

    // Live-Sekundenzahl seit Start (für UI-Smoothness)
    const liveSeconds = Math.floor((Date.now() - progress.startedAt) / 1000);
    const isActive = progress.state === 'accepted' || progress.state === 'in_progress';
    const isSuccess = progress.state === 'successful';
    const isFailed = progress.state === 'failed';

    // Nächstes Polling in X Sek
    const POLL_INTERVAL = 60;
    const lastPollSecond = progress.pollNumber * POLL_INTERVAL;
    const nextPollIn = isActive ? Math.max(0, lastPollSecond + POLL_INTERVAL - liveSeconds) : 0;

    let stateLabel = '';
    let stateColor = '';
    let StateIcon: any = Clock;
    if (progress.state === 'accepted') {
      stateLabel = 'Job angenommen, wird verarbeitet';
      stateColor = '#3B82F6';
      StateIcon = Clock;
    } else if (progress.state === 'in_progress') {
      stateLabel = 'In Bearbeitung im DATEV-Rechenzentrum';
      stateColor = '#3B82F6';
      StateIcon = RefreshCw;
    } else if (progress.state === 'successful') {
      stateLabel = 'Erfolgreich abgeschlossen';
      stateColor = '#10B981';
      StateIcon = CheckCircle;
    } else if (progress.state === 'failed') {
      stateLabel = 'Fehlgeschlagen';
      stateColor = '#EF4444';
      StateIcon = XCircle;
    }

    return (
      <div style={{
        backgroundColor: '#F9FAFB',
        border: `1px solid ${isFailed ? '#FECACA' : isSuccess ? '#BBF7D0' : '#DBEAFE'}`,
        borderRadius: '6px',
        padding: '16px',
        marginTop: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <StateIcon
            size={18}
            color={stateColor}
            style={isActive && StateIcon === RefreshCw ? { animation: 'spin 1s linear infinite' } : undefined}
          />
          <strong style={{ fontSize: '14px', color: '#111827' }}>{stateLabel}</strong>
        </div>

        <div style={{ fontSize: '13px', color: '#6B7280', marginLeft: '26px' }}>
          Job-UUID: <code style={{ fontFamily: 'monospace', fontSize: '12px' }}>{progress.jobUuid}</code>
        </div>

        <div style={{ fontSize: '13px', color: '#6B7280', marginLeft: '26px', marginTop: '4px' }}>
          Vergangene Zeit: <strong>{liveSeconds}s</strong>
          {progress.pollNumber > 0 && <> · Polling-Anfragen: <strong>{progress.pollNumber}</strong></>}
        </div>

        {isActive && (
          <div style={{
            fontSize: '12px',
            color: '#1E40AF',
            marginLeft: '26px',
            marginTop: '8px',
            backgroundColor: '#EFF6FF',
            padding: '8px 12px',
            borderRadius: '4px',
            display: 'inline-block'
          }}>
            {nextPollIn > 0
              ? <>Nächste Status-Abfrage in {nextPollIn} Sekunden (DATEV-Polling-Limit: 1 Anfrage/Min)</>
              : <>Status wird gerade abgefragt...</>}
          </div>
        )}
      </div>
    );
  };

  const renderError = (errorMsg: string | null, helpUrl: string | null) => {
    if (!errorMsg) return null;
    return (
      <div style={{
        backgroundColor: '#FEE2E2',
        border: '1px solid #FECACA',
        borderRadius: '6px',
        padding: '12px',
        marginTop: '12px',
        fontSize: '14px',
        color: '#991B1B',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px'
      }}>
        <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '1px' }} />
        <div>
          {errorMsg}
          {helpUrl && (
            <div style={{ marginTop: '4px' }}>
              <a href={helpUrl} target="_blank" rel="noopener noreferrer"
                 style={{ color: '#1E40AF', textDecoration: 'underline', fontSize: '13px' }}>
                DATEV-Hilfe öffnen
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    backgroundColor: 'white',
    width: '100%',
    boxSizing: 'border-box'
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '4px'
  };

  const sectionStyle: React.CSSProperties = {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    margin: '20px 0 10px 0',
    paddingBottom: '6px',
    borderBottom: '1px solid #E5E7EB'
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
    marginBottom: '8px',
    alignItems: 'end'
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <div>
      {/* Header */}
      <div style={{
        backgroundColor: '#EFF6FF',
        border: '1px solid #DBEAFE',
        borderRadius: '6px',
        padding: '12px',
        marginBottom: '20px',
        fontSize: '14px',
        color: '#1E40AF'
      }}>
        <strong>DATEV Lohnaustauschdatenservice (hr:exchange):</strong> Bidirektionaler Datenaustausch
        mit dem DATEV-Lohnsystem für Bestand <strong>{clientId}</strong>.
        <span style={{ marginLeft: '8px', fontSize: '12px', color: '#1E3A8A' }}>
          · Lohnsystem: <strong>{targetSystem.toUpperCase()}</strong>
        </span>
      </div>

      {/* Berechtigungsprüfung */}
      {!accessChecked && (
        <div style={{ padding: '24px', textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>
          <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: '8px' }} />
          Berechtigung wird geprüft...
        </div>
      )}

      {accessChecked && !hasAccess && (
        <div style={{
          backgroundColor: '#FEE2E2',
          border: '1px solid #FECACA',
          borderRadius: '6px',
          padding: '16px',
          marginBottom: '16px',
          fontSize: '14px',
          color: '#991B1B',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px'
        }}>
          <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <strong>Keine Berechtigung für diesen Bestand.</strong>
            <div style={{ marginTop: '4px' }}>{accessError}</div>
            {accessHelpUrl && (
              <div style={{ marginTop: '6px' }}>
                <a href={accessHelpUrl} target="_blank" rel="noopener noreferrer"
                   style={{ color: '#1E40AF', textDecoration: 'underline', fontSize: '13px' }}>
                  DATEV-Hilfe öffnen
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audit-Auflage 2: Freigegebene Mandanten bei Berechtigungsfehler */}
      {availableClients && availableClients.length > 0 && !hasAccess && accessChecked && (
        <div style={{
          backgroundColor: '#FEF3C7',
          border: '1px solid #FDE68A',
          borderRadius: '6px',
          padding: '14px',
          marginBottom: '16px',
          fontSize: '13px',
          color: '#78350F'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '8px' }}>
            Für diesen Scope freigegebene Mandanten:
          </div>
          {availableClients.map((group, idx) => (
            <div key={idx} style={{ marginBottom: '6px' }}>
              <span style={{ fontWeight: '500' }}>Beraternummer {group.consultant}:</span>{' '}
              Mandanten {group.ranges.join(', ')}
              <span style={{ color: '#92400E', fontSize: '12px', marginLeft: '8px' }}>
                ({group.count} Mandanten)
              </span>
            </div>
          ))}
          <div style={{ marginTop: '10px', fontStyle: 'italic', color: '#92400E', fontSize: '12px' }}>
            Wenden Sie sich an Ihren Steuerberater, falls Mandanten fehlen.
          </div>
        </div>
      )}

      {/* Hauptinhalt nur bei Berechtigung */}
      {accessChecked && hasAccess && (
        <>
          {/* Bestätigung */}
          <div style={{
            backgroundColor: '#F0FDF4',
            border: '1px solid #BBF7D0',
            borderRadius: '6px',
            padding: '10px 12px',
            marginBottom: '20px',
            fontSize: '13px',
            color: '#166534'
          }}>
            ✓ Berechtigung für Bestand {clientId} bestätigt (Target-System: {targetSystem.toUpperCase()})
          </div>

          {/* View-Auswahl */}
          {view === 'overview' && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '16px'
            }}>
              <button
                onClick={() => setView('read')}
                style={{
                  textAlign: 'left',
                  padding: '20px',
                  backgroundColor: 'white',
                  border: '2px solid #E5E7EB',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#3B82F6'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#E5E7EB'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Users size={20} color="#3B82F6" />
                  <strong style={{ fontSize: '15px', color: '#111827' }}>Daten abrufen</strong>
                </div>
                <div style={{ fontSize: '13px', color: '#6B7280' }}>
                  Liest Mandantenstammdaten, Mitarbeiterdaten oder Bewegungsdaten aus dem DATEV-Lohnsystem (asynchron via Job)
                </div>
              </button>

              <button
                onClick={() => setView('create')}
                style={{
                  textAlign: 'left',
                  padding: '20px',
                  backgroundColor: 'white',
                  border: '2px solid #E5E7EB',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#10B981'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#E5E7EB'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <UserPlus size={20} color="#10B981" />
                  <strong style={{ fontSize: '15px', color: '#111827' }}>Mitarbeiter anlegen</strong>
                </div>
                <div style={{ fontSize: '13px', color: '#6B7280' }}>
                  Legt einen neuen Mitarbeiter im DATEV-Lohnsystem an (asynchron mit Job-Validierung)
                </div>
              </button>
            </div>
          )}

          {/* Lese-Workflow */}
          {view === 'read' && (
            <div>
              <button
                onClick={() => setView('overview')}
                style={{
                  background: 'none', border: 'none', color: '#6B7280',
                  fontSize: '13px', cursor: 'pointer', marginBottom: '16px',
                  padding: 0
                }}
              >
                ← Zurück zur Übersicht
              </button>

              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#111827' }}>
                Daten aus DATEV-Lohnsystem abrufen
              </h3>

              <div style={{
                backgroundColor: '#FEF3C7',
                border: '1px solid #FDE68A',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '20px',
                fontSize: '13px',
                color: '#92400E'
              }}>
                <strong>Asynchroner Workflow:</strong> Der Abruf läuft in 3 Phasen:
                Job anlegen → Status pollen (max. 1×/Min, max. 15 Min) → Ergebnis abholen.
                DATEV MUST: Alle drei Ressourcentypen (client-data, employees, month-records) müssen abrufbar sein.
              </div>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '16px', flexWrap: 'wrap' }}>
                <div style={{ flex: '0 0 220px' }}>
                  <label style={labelStyle}>Ressourcentyp</label>
                  <select
                    value={readResourceName}
                    onChange={e => setReadResourceName(e.target.value)}
                    style={inputStyle}
                  >
                    <option value="employees">Mitarbeiterdaten (employees)</option>
                    <option value="client-data">Mandantenstammdaten (client-data)</option>
                    <option value="month-records">Bewegungsdaten (month-records)</option>
                  </select>
                </div>
                <div style={{ flex: '0 0 200px' }}>
                  <label style={labelStyle}>Reference-Date (Format JJJJ-MM)</label>
                  <input
                    type="text"
                    value={readMonth}
                    onChange={e => setReadMonth(e.target.value)}
                    placeholder="2026-06"
                    style={inputStyle}
                  />
                </div>
                <button
                  onClick={startReadEmployees}
                  disabled={readJobProgress?.state === 'accepted' || readJobProgress?.state === 'in_progress'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '8px 16px',
                    backgroundColor: readJobProgress?.state === 'accepted' || readJobProgress?.state === 'in_progress' ? '#9CA3AF' : '#3B82F6',
                    color: 'white', border: 'none', borderRadius: '6px',
                    cursor: readJobProgress?.state === 'accepted' || readJobProgress?.state === 'in_progress' ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  <Search size={16} />
                  Abruf starten
                </button>
              </div>

              {renderJobProgress(readJobProgress)}
              {renderError(readError, readHelpUrl)}

              {readResult && (
                <div style={{ marginTop: '16px' }}>
                  <h4 style={{ fontSize: '14px', color: '#111827', margin: '0 0 12px 0' }}>
                    Ergebnis für {readResourceName}:
                  </h4>

                  {Array.isArray(readResult.errors) && readResult.errors.length > 0 && (
                    <DatevErrorPanel errors={readResult.errors} />
                  )}

                  {readResourceName === 'employees' && Array.isArray(readResult.employees) && readResult.employees.map((emp: DatevExchangeEmployee, idx: number) => (
                    <div key={idx} style={{ marginBottom: '24px' }}>
                      <EmployeeCard employee={emp} />
                    </div>
                  ))}

                  {readResourceName !== 'employees' && (
                    <pre style={{
                      margin: 0, padding: '12px',
                      backgroundColor: '#1F2937', color: '#F9FAFB',
                      borderRadius: '4px', overflowX: 'auto',
                      maxHeight: '500px', overflowY: 'auto',
                      fontSize: '11px', lineHeight: '1.5',
                      fontFamily: 'Menlo, Monaco, "Courier New", monospace'
                    }}>
                      {JSON.stringify(verdecke(readResult), null, 2)}
                    </pre>
                  )}

                  <details style={{ marginTop: '16px' }}>
                    <summary style={{ cursor: 'pointer', fontSize: '13px', color: '#6B7280' }}>
                      Vollständige DATEV-Rohantwort anzeigen
                    </summary>
                    <pre style={{
                      margin: '8px 0 0 0', padding: '12px',
                      backgroundColor: '#1F2937', color: '#F9FAFB',
                      borderRadius: '4px', overflowX: 'auto',
                      maxHeight: '400px', overflowY: 'auto',
                      fontSize: '11px', lineHeight: '1.5',
                      fontFamily: 'Menlo, Monaco, "Courier New", monospace'
                    }}>
                      {JSON.stringify(verdecke(readResult), null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          )}

          {/* Schreibe-Workflow */}
          {view === 'create' && (
            <div>
              <button
                onClick={() => setView('overview')}
                style={{
                  background: 'none', border: 'none', color: '#6B7280',
                  fontSize: '13px', cursor: 'pointer', marginBottom: '16px',
                  padding: 0
                }}
              >
                ← Zurück zur Übersicht
              </button>

              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#111827' }}>
                Neuen Mitarbeiter anlegen
              </h3>

              <div style={{
                backgroundColor: '#EDE9FE',
                border: '1px solid #DDD6FE',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '20px',
                fontSize: '13px',
                color: '#5B21B6'
              }}>
                <strong>Asynchroner Schreibvorgang:</strong> Die Anlage wird als Job an das DATEV-Rechenzentrum
                übergeben. Der Status wird automatisch gepollt (1×/Min, max. 15 Min).
                Alle DATEV-Pflichtfelder (MUST) sind vorbefüllt mit gültigen Demo-Werten und editierbar.
              </div>

              {/* Grunddaten */}
              <div style={sectionStyle}>Grunddaten</div>
              <div style={gridStyle}>
                <div>
                  <label style={labelStyle}>Nachname *</label>
                  <input type="text" value={newEmpSurname} onChange={e => setNewEmpSurname(e.target.value)} placeholder="Mustermann" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Vorname *</label>
                  <input type="text" value={newEmpFirstName} onChange={e => setNewEmpFirstName(e.target.value)} placeholder="Max" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Geschlecht *</label>
                  <select value={newEmpSex} onChange={e => setNewEmpSex(e.target.value as 'M' | 'W' | 'D')} style={inputStyle}>
                    <option value="W">Weiblich (W)</option>
                    <option value="M">Männlich (M)</option>
                    <option value="D">Divers (D)</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Betriebliche Personalnummer *</label>
                  <input type="text" value={newEmpCompanyPnr} onChange={e => setNewEmpCompanyPnr(e.target.value)} placeholder="BX1dd2" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Beschäftigungsbetrieb (optional)</label>
                  <input type="text" value={newEmpBusinessUnitId} onChange={e => setNewEmpBusinessUnitId(e.target.value)} placeholder="Leer bei LODAS ohne Betriebsstätten" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Zahlungsart *</label>
                  <input type="text" value={newEmpPaymentMethod} onChange={e => setNewEmpPaymentMethod(e.target.value)} placeholder="5" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Personalnummer (optional)</label>
                  <input type="text" value={newEmpPnr} onChange={e => { setNewEmpPnr(e.target.value); setDuplicateWarning(null); }} placeholder="Leer = automatisch" style={inputStyle} />
                </div>
              </div>

              {/* Beschäftigungszeitraum */}
              <div style={sectionStyle}>Beschäftigungszeitraum</div>
              <div style={gridStyle}>
                <div>
                  <label style={labelStyle}>Eintrittsdatum * (JJJJ-MM-TT)</label>
                  <input type="text" value={newEmpStartDate} onChange={e => setNewEmpStartDate(e.target.value)} placeholder="2026-06-01" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Austrittsdatum * (JJJJ-MM-TT)</label>
                  <input type="text" value={newEmpEndDate} onChange={e => setNewEmpEndDate(e.target.value)} placeholder="2099-12-31" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Reference-Date * (JJJJ-MM)</label>
                  <input type="text" value={newEmpRefDate} onChange={e => setNewEmpRefDate(e.target.value)} placeholder="2026-06" style={inputStyle} />
                </div>
              </div>

              {/* Persönliche Angaben */}
              <div style={sectionStyle}>Persönliche Angaben</div>
              <div style={gridStyle}>
                <div>
                  <label style={labelStyle}>E-Mail *</label>
                  <input type="text" value={newEmpEmail} onChange={e => setNewEmpEmail(e.target.value)} placeholder="max@example.de" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Telefon *</label>
                  <input type="text" value={newEmpPhone} onChange={e => setNewEmpPhone(e.target.value)} placeholder="+49 ..." style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>SV-Nummer *</label>
                  <input type="text" value={newEmpSsn} onChange={e => setNewEmpSsn(e.target.value)} placeholder="15070649C103" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Geburtsdatum * (JJJJ-MM-TT)</label>
                  <input type="text" value={newEmpDob} onChange={e => setNewEmpDob(e.target.value)} placeholder="2000-01-02" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Staatsangehörigkeit * (Schlüssel)</label>
                  <input type="text" value={newEmpNationality} onChange={e => setNewEmpNationality(e.target.value)} placeholder="000" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Vorsatzwort (optional)</label>
                  <input type="text" value={newEmpNamePrefix} onChange={e => setNewEmpNamePrefix(e.target.value)} placeholder="z. B. von" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Namenszusatz (optional)</label>
                  <input type="text" value={newEmpNameAffix} onChange={e => setNewEmpNameAffix(e.target.value)} placeholder="z. B. Baron" style={inputStyle} />
                </div>
              </div>

              {/* Adresse */}
              <div style={sectionStyle}>Adresse</div>
              <div style={gridStyle}>
                <div>
                  <label style={labelStyle}>Straße *</label>
                  <input type="text" value={newEmpStreet} onChange={e => setNewEmpStreet(e.target.value)} placeholder="Bahnhofstr." style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Hausnummer *</label>
                  <input type="text" value={newEmpHouseNumber} onChange={e => setNewEmpHouseNumber(e.target.value)} placeholder="12a" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>PLZ *</label>
                  <input type="text" value={newEmpPostalCode} onChange={e => setNewEmpPostalCode(e.target.value)} placeholder="90429" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Ort *</label>
                  <input type="text" value={newEmpCity} onChange={e => setNewEmpCity(e.target.value)} placeholder="Nuernberg" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Land * (Schlüssel)</label>
                  <input type="text" value={newEmpCountry} onChange={e => setNewEmpCountry(e.target.value)} placeholder="D" style={inputStyle} />
                </div>
              </div>

              {/* Bankverbindung */}
              <div style={sectionStyle}>Bankverbindung</div>
              <div style={gridStyle}>
                <div>
                  <label style={labelStyle}>IBAN *</label>
                  <input type="text" value={newEmpIban} onChange={e => setNewEmpIban(e.target.value)} placeholder="DE..." style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>BIC *</label>
                  <input type="text" value={newEmpBic} onChange={e => setNewEmpBic(e.target.value)} placeholder="GENODEF1P17" style={inputStyle} />
                </div>
              </div>

              {/* Arbeitszeit */}
              <div style={sectionStyle}>Arbeitszeit (Stunden pro Tag)</div>
              <div style={gridStyle}>
                <div>
                  <label style={labelStyle}>Wöchentliche Arbeitszeit *</label>
                  <input type="text" value={newEmpWeeklyHours} onChange={e => setNewEmpWeeklyHours(e.target.value)} placeholder="25.00" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Montag *</label>
                  <input type="text" value={newEmpHoursMon} onChange={e => setNewEmpHoursMon(e.target.value)} placeholder="5.00" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Dienstag *</label>
                  <input type="text" value={newEmpHoursTue} onChange={e => setNewEmpHoursTue(e.target.value)} placeholder="5.00" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Mittwoch *</label>
                  <input type="text" value={newEmpHoursWed} onChange={e => setNewEmpHoursWed(e.target.value)} placeholder="5.00" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Donnerstag *</label>
                  <input type="text" value={newEmpHoursThu} onChange={e => setNewEmpHoursThu(e.target.value)} placeholder="5.00" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Freitag *</label>
                  <input type="text" value={newEmpHoursFri} onChange={e => setNewEmpHoursFri(e.target.value)} placeholder="5.00" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Samstag *</label>
                  <input type="text" value={newEmpHoursSat} onChange={e => setNewEmpHoursSat(e.target.value)} placeholder="0.00" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Sonntag *</label>
                  <input type="text" value={newEmpHoursSun} onChange={e => setNewEmpHoursSun(e.target.value)} placeholder="0.00" style={inputStyle} />
                </div>
              </div>

              {/* Tätigkeit */}
              <div style={sectionStyle}>Tätigkeit / Tätigkeitsschlüssel</div>
              <div style={gridStyle}>
                <div>
                  <label style={labelStyle}>Arbeitnehmertyp * (Schlüssel)</label>
                  <input type="text" value={newEmpActivityType} onChange={e => setNewEmpActivityType(e.target.value)} placeholder="1" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Personengruppenschlüssel * (101–900)</label>
                  <input type="text" value={newEmpEmployeeType} onChange={e => setNewEmpEmployeeType(e.target.value)} placeholder="101" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Arbeitnehmerüberlassung * (Schlüssel)</label>
                  <input type="text" value={newEmpPersonnelLeasing} onChange={e => setNewEmpPersonnelLeasing(e.target.value)} placeholder="0" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Ausgeübte Tätigkeit *</label>
                  <input type="text" value={newEmpOccupationalTitle} onChange={e => setNewEmpOccupationalTitle(e.target.value)} placeholder="DevOps Engineer" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Höchster Ausbildungsabschluss * (Schlüssel)</label>
                  <input type="text" value={newEmpHighestProfTraining} onChange={e => setNewEmpHighestProfTraining(e.target.value)} placeholder="2" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Höchster Schulabschluss * (Schlüssel)</label>
                  <input type="text" value={newEmpHighestEducation} onChange={e => setNewEmpHighestEducation(e.target.value)} placeholder="2" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Vertragsform * (Schlüssel)</label>
                  <input type="text" value={newEmpContractualStructure} onChange={e => setNewEmpContractualStructure(e.target.value)} placeholder="1" style={inputStyle} />
                </div>
              </div>

              {/* Steuer */}
              <div style={sectionStyle}>Steueridentifikationsmerkmale</div>
              <div style={gridStyle}>
                <div>
                  <label style={labelStyle}>Steuer-Identifikationsnummer *</label>
                  <input type="text" value={newEmpTaxId} onChange={e => setNewEmpTaxId(e.target.value)} placeholder="12345678912" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Kennzeichnung Arbeitgeber * (Haupt/Neben)</label>
                  <input type="text" value={newEmpEmploymentType} onChange={e => setNewEmpEmploymentType(e.target.value)} placeholder="2" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Gewünschter jährl. Freibetrag *</label>
                  <input type="text" value={newEmpRequestedAllowance} onChange={e => setNewEmpRequestedAllowance(e.target.value)} placeholder="123" style={inputStyle} />
                </div>
              </div>

              {/* Urlaub */}
              <div style={sectionStyle}>Urlaubsanspruch</div>
              <div style={gridStyle}>
                <div>
                  <label style={labelStyle}>Grundsätzlicher Urlaubsanspruch/Jahr *</label>
                  <input type="text" value={newEmpBasicVacation} onChange={e => setNewEmpBasicVacation(e.target.value)} placeholder="30.0" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Urlaubsanspruch aktuelles Jahr *</label>
                  <input type="text" value={newEmpCurrentYearVacation} onChange={e => setNewEmpCurrentYearVacation(e.target.value)} placeholder="30.0" style={inputStyle} />
                </div>
              </div>

              {/* Optionale Bezüge */}
              <div style={sectionStyle}>Bezüge (optional — Festbezug ODER Stundenlohn)</div>
              <div style={gridStyle}>
                <div>
                  <label style={labelStyle}>Festbezug: Betrag</label>
                  <input type="text" value={newEmpGrossAmount} onChange={e => setNewEmpGrossAmount(e.target.value)} placeholder="4200.00" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Festbezug: Lohnart-ID</label>
                  <input type="text" value={newEmpGrossSalaryTypeId} onChange={e => setNewEmpGrossSalaryTypeId(e.target.value)} placeholder="200" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Festbezug: Intervall (Monate)</label>
                  <input type="text" value={newEmpGrossPaymentMonths} onChange={e => setNewEmpGrossPaymentMonths(e.target.value)} placeholder="1, 4, 7, 10" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Festbezug: ID</label>
                  <input type="text" value={newEmpGrossId} onChange={e => setNewEmpGrossId(e.target.value)} placeholder="1" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Stundenlohn: Betrag</label>
                  <input type="text" value={newEmpHourlyAmount} onChange={e => setNewEmpHourlyAmount(e.target.value)} placeholder="13.37" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Stundenlohn: ID</label>
                  <input type="text" value={newEmpHourlyId} onChange={e => setNewEmpHourlyId(e.target.value)} placeholder="1" style={inputStyle} />
                </div>
              </div>

              {duplicateWarning && (
                <div style={{
                  backgroundColor: '#FEF3C7',
                  border: '1px solid #FDE68A',
                  borderRadius: '6px',
                  padding: '12px',
                  margin: '16px 0',
                  fontSize: '13px',
                  color: '#92400E',
                  display: 'flex', alignItems: 'flex-start', gap: '8px'
                }}>
                  <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
                  <div>
                    <strong>Duplikat-Warnung (DATEV MUST):</strong> {duplicateWarning}
                  </div>
                </div>
              )}

              <button
                onClick={startCreateEmployee}
                disabled={createJobProgress?.state === 'accepted' || createJobProgress?.state === 'in_progress'}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '10px 18px',
                  marginTop: '16px',
                  backgroundColor: createJobProgress?.state === 'accepted' || createJobProgress?.state === 'in_progress' ? '#9CA3AF' : '#10B981',
                  color: 'white', border: 'none', borderRadius: '6px',
                  cursor: createJobProgress?.state === 'accepted' || createJobProgress?.state === 'in_progress' ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                <ArrowRight size={16} />
                Mitarbeiter im DATEV-Lohnsystem anlegen
              </button>

              {renderJobProgress(createJobProgress)}
              {renderError(createError, createHelpUrl)}

              {createResult && (
                <div style={{ marginTop: '16px' }}>
                  <h4 style={{ fontSize: '14px', color: '#111827', margin: '0 0 12px 0' }}>
                    Ergebnis:
                  </h4>

                  {Array.isArray(createResult.employees) && createResult.employees.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <EmployeeCard
                        employee={createResult.employees[0]}
                        badge="Mitarbeiter angelegt"
                        footnote={
                          'Der angezeigte Datensatz stammt aus der DATEV-Antwort zur Anlage. ' +
                          'Die Persistenz-Prüfung unten verifiziert den tatsächlich im Lohnsystem gespeicherten Stand.'
                        }
                      />
                    </div>
                  )}

                  {Array.isArray(createResult.errors) && createResult.errors.length > 0 && (
                    <DatevErrorPanel errors={createResult.errors} />
                  )}

                  {/* DATEV MUST: Persistenz-Prüfung nach Neuanlage */}
                  {Array.isArray(createResult.employees) && createResult.employees.length > 0 && (
                    <div style={{
                      backgroundColor: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      padding: '14px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ fontSize: '13px', color: '#374151', marginBottom: '8px' }}>
                        <strong>Persistenz-Prüfung (DATEV MUST):</strong> Nach jeder Neuanlage muss ein
                        erneuter Leseabruf durchgeführt werden, um die Persistenz der Daten im DATEV-System zu verifizieren.
                      </div>
                      <button
                        onClick={startPersistenceCheck}
                        disabled={persistenceLoading}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '8px 14px',
                          backgroundColor: persistenceLoading ? '#9CA3AF' : '#3B82F6',
                          color: 'white', border: 'none', borderRadius: '6px',
                          cursor: persistenceLoading ? 'not-allowed' : 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        {persistenceLoading ? (
                          <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Wird geprüft...</>
                        ) : (
                          <><Search size={14} /> Persistenz prüfen (Mitarbeiter erneut lesen)</>
                        )}
                      </button>

                      {persistenceResult && (
                        <div style={{
                          marginTop: '12px',
                          backgroundColor: '#F0FDF4',
                          border: '1px solid #BBF7D0',
                          borderRadius: '6px',
                          padding: '10px 12px',
                          fontSize: '13px',
                          color: '#166534'
                        }}>
                          <strong>Persistenz bestätigt:</strong> Leseabruf liefert {persistenceResult.employees?.length || 0} Mitarbeiter
                          — der Abruf verifiziert den tatsächlich im DATEV-Lohnsystem gespeicherten Datenstand.
                        </div>
                      )}

                      {persistenceError && (
                        <div style={{
                          marginTop: '12px',
                          backgroundColor: '#FEE2E2',
                          border: '1px solid #FECACA',
                          borderRadius: '6px',
                          padding: '10px 12px',
                          fontSize: '13px',
                          color: '#991B1B'
                        }}>
                          Persistenz-Prüfung fehlgeschlagen: {persistenceError}
                        </div>
                      )}
                    </div>
                  )}

                  <details>
                    <summary style={{ cursor: 'pointer', fontSize: '13px', color: '#6B7280' }}>
                      Vollständige DATEV-Rohantwort anzeigen
                    </summary>
                    <pre style={{
                      margin: '8px 0 0 0', padding: '12px',
                      backgroundColor: '#1F2937', color: '#F9FAFB',
                      borderRadius: '4px', overflowX: 'auto',
                      maxHeight: '300px', overflowY: 'auto',
                      fontSize: '11px', lineHeight: '1.5',
                      fontFamily: 'Menlo, Monaco, "Courier New", monospace'
                    }}>
                      {JSON.stringify(verdecke(createResult), null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Tick verwenden, damit Component bei jedem Sekundenschlag re-rendert (für Live-Countdown) */}
      <div style={{ display: 'none' }}>{tick}</div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <ScopedMonthEmployeeSyncPanel customerId={customerId} beraternummer={beraternummer} mandantennummer={mandantennummer} />
    </div>
  );
};
